// src/pages/user/sub-admin-list/index.jsx
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Col,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Form,
  FormFeedback
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import TableContainer from "../../../components/Common/TableContainer";
import Paginations from "../../../components/Common/Pagination";
import Spinners from "../../../components/Common/Spinner";
import {
  useGetAllSubAdmins,
  useBlockUnblockUser,
  useCreateSubAdmin,
  useUpdateSubAdmin
} from "../../../api/users";
import toastr from "toastr";
import { useFormik } from "formik";
import * as Yup from "yup";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";

const SubAdminList = () => {
  document.title = "Sub Admins List | Admin";

  // server-side controls
  const [page, setPage] = useState(1); // 1-based
  const [limit, setLimit] = useState(10);

  // modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedSubAdmin, setSelectedSubAdmin] = useState(null);

  // mutation hook (GET)
  const { mutate, data, isLoading, isPending, error } = useGetAllSubAdmins();
  const loading = isLoading || isPending;

  // block/unblock mutation
  const blockUnblockMutation = useBlockUnblockUser();

  // create sub-admin mutation
  const createSubAdminMutation = useCreateSubAdmin();

  // update sub-admin mutation
  const updateSubAdminMutation = useUpdateSubAdmin();

  // Formik for create form
  const createFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      fullName: "",
      email: "",
      password: "",
      phoneNumber: "",
    },
    validationSchema: Yup.object({
      fullName: Yup.string().required("Full Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .required("Password is required"),
      phoneNumber: Yup.string().required("Phone Number is required"),
    }),
    onSubmit: (values) => {
      // Normalize phone number to include "+"
      const withPlus = (d) => (d ? (d.startsWith("+") ? d : `+${d}`) : d);

      const payload = {
        fullName: values.fullName,
        email: values.email,
        password: values.password,
        phoneNumber: withPlus(values.phoneNumber),
        status: "approved" // hardcoded
      };

      createSubAdminMutation.mutate(payload, {
        onSuccess: () => {
          toastr.success("Sub-admin created successfully!");
          setShowCreateModal(false);
          createFormik.resetForm();
          // Refresh the list
          mutate({ page, limit });
        },
        onError: () => {
          // Error toast is handled by global axios interceptor
        }
      });
    }
  });

  // Formik for update form
  const updateFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      fullName: selectedSubAdmin?.fullName || "",
      email: selectedSubAdmin?.email || "",
      phoneNumber: selectedSubAdmin?.phoneNumber || "",
    },
    validationSchema: Yup.object({
      fullName: Yup.string().required("Full Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      phoneNumber: Yup.string().required("Phone Number is required"),
    }),
    onSubmit: (values) => {
      if (!selectedSubAdmin?.slug) return;

      // Normalize phone number to include "+"
      const withPlus = (d) => (d ? (d.startsWith("+") ? d : `+${d}`) : d);

      // status is hardcoded to "approved" but not sent in payload
      const payload = {
        fullName: values.fullName,
        email: values.email,
        phoneNumber: withPlus(values.phoneNumber),
      };

      updateSubAdminMutation.mutate(
        { slug: selectedSubAdmin.slug, payload },
        {
          onSuccess: () => {
            toastr.success("Sub-admin updated successfully!");
            setShowUpdateModal(false);
            setSelectedSubAdmin(null);
            updateFormik.resetForm();
            // Refresh the list
            mutate({ page, limit });
          },
          onError: () => {
            // Error toast is handled by global axios interceptor
          }
        }
      );
    }
  });

  // Handle edit button click
  const handleEdit = useCallback((subAdmin) => {
    setSelectedSubAdmin(subAdmin);
    setShowUpdateModal(true);
  }, []);

  // Update formik values when selectedSubAdmin changes
  useEffect(() => {
    if (selectedSubAdmin) {
      // Remove "+" prefix from phone number for UI (PhoneInput expects digits only)
      const phoneForUI = (selectedSubAdmin.phoneNumber || "").replace(/^\+/, "");
      updateFormik.setValues({
        fullName: selectedSubAdmin.fullName || "",
        email: selectedSubAdmin.email || "",
        phoneNumber: phoneForUI,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubAdmin]);

  // Build a stable fetch key and guard against duplicate calls (e.g., StrictMode double invoke)
  const lastFetchKeyRef = useRef("");
  const fetchKey = JSON.stringify({
    page,
    limit
  });

  // fire fetch whenever fetchKey changes
  useEffect(() => {
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    mutate({
      page,
      limit
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey]);

  // API response structure: { status: "success", data: [...], totalRecords: 2, results: 2 }
  const subAdmins = Array.isArray(data?.data) ? data.data : [];
  const total = data?.totalRecords ?? data?.results ?? 0;

  // Handle toggle block/unblock
  const handleToggleBlock = useCallback(
    (slug, currentStatus) => {
      const newStatus = !currentStatus;
      blockUnblockMutation.mutate(
        { slug, status: newStatus },
        {
          onSuccess: () => {
            toastr.success(`Sub-admin ${newStatus ? "blocked" : "unblocked"} successfully`);
            // Refresh the list
            mutate({ page, limit });
          },
          onError: () => {
            // Error toast is handled by global axios interceptor
          }
        }
      );
    },
    [blockUnblockMutation, mutate, page, limit]
  );

  const columns = useMemo(
    () => [
      { header: "Full Name", accessorKey: "fullName", enableColumnFilter: false },
      { header: "Email", accessorKey: "email", enableColumnFilter: false },
      { header: "Phone Number", accessorKey: "phoneNumber", enableColumnFilter: false },
      {
        header: "Blocked",
        accessorKey: "isBlockedByAdmin",
        enableColumnFilter: false,
        cell: (info) => {
          const subAdmin = info.row.original;
          const isBlocked = !!subAdmin.isBlockedByAdmin;
          const slug = subAdmin.slug;
          const isUpdating = blockUnblockMutation.isPending;

          return (
            <div className="form-check form-switch" style={{ fontSize: "1.1rem" }}>
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id={`block-${slug}`}
                checked={isBlocked}
                disabled={isUpdating}
                onChange={() => handleToggleBlock(slug, isBlocked)}
                style={{
                  width: "3rem",
                  height: "1.5rem",
                  cursor: isUpdating ? "not-allowed" : "pointer"
                }}
              />
              <label
                className="form-check-label ms-2"
                htmlFor={`block-${slug}`}
                style={{ fontSize: "0.95rem" }}
              >
                {isBlocked ? "Blocked" : "Active"}
              </label>
            </div>
          );
        }
      },
      {
        header: "Updated At",
        accessorKey: "updatedAt",
        enableColumnFilter: false,
        cell: (info) => (info.getValue() ? new Date(info.getValue()).toLocaleDateString() : "-")
      },
      {
        header: "Created At",
        accessorKey: "createdAt",
        enableColumnFilter: false,
        cell: (info) => (info.getValue() ? new Date(info.getValue()).toLocaleDateString() : "-")
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => {
          const subAdmin = info.row.original;
          return (
            <Button color="primary" size="sm" onClick={() => handleEdit(subAdmin)}>
              <i className="mdi mdi-pencil me-1" />
              Edit
            </Button>
          );
        }
      }
    ],
    [handleToggleBlock, blockUnblockMutation.isPending, handleEdit]
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Sub Admins" breadcrumbItem="Sub Admins List" />

        <Row className="mb-3 g-2 align-items-center">
          <Col md="auto">
            <Button color="primary" onClick={() => setShowCreateModal(true)} disabled={loading}>
              <i className="mdi mdi-plus me-1" />
              Create Sub Admin
            </Button>
          </Col>
        </Row>

        <Row className="mb-3 g-2 align-items-center">
          <Col md="auto" className="ms-auto d-flex gap-2 align-items-center">
            <Button
              color="secondary"
              outline
              onClick={() => {
                setLimit(10);
                setPage(1);
              }}
              disabled={loading}
            >
              Reset
            </Button>
            {/* Page size */}
            <Input
              type="select"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              style={{ width: "auto", minWidth: "100px" }}
            >
              {[10, 20].map((n) => (
                <option key={n} value={n}>
                  Show {n}
                </option>
              ))}
            </Input>
            <Button
              color="light"
              onClick={() =>
                mutate({
                  page,
                  limit
                })
              }
              disabled={loading}
            >
              <i className="mdi mdi-refresh" />
            </Button>
          </Col>
        </Row>

        <Card>
          <CardBody>
            {loading && subAdmins.length === 0 ? (
              <Spinners setLoading={() => {}} />
            ) : error ? (
              <div className="text-danger">Failed to load sub-admins.</div>
            ) : (
              <>
                <TableContainer
                  columns={columns}
                  data={subAdmins}
                  // server-side: disable client pagination & global filters
                  isPagination={false}
                  isCustomPageSize={false}
                  isGlobalFilter={false}
                  isJobListGlobalFilter={false}
                  tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-2 border-top"
                  theadClass="table-light"
                  divClassName="table-responsive"
                />

                <Paginations
                  total={total}
                  perPageData={limit}
                  currentPage={page}
                  setCurrentPage={setPage}
                  isShowingPageLength
                  paginationDiv="dataTables_paginate paging_simple_numbers pagination-rounded d-flex justify-content-end mt-3"
                  paginationClass="pagination"
                />
              </>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Create Sub Admin Modal */}
      <Modal isOpen={showCreateModal} toggle={() => setShowCreateModal(false)}>
        <ModalHeader toggle={() => setShowCreateModal(false)}>Create Sub Admin</ModalHeader>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            createFormik.handleSubmit();
          }}
        >
          <ModalBody>
            <div className="mb-3">
              <Label for="fullName">Full Name</Label>
              <Input
                id="fullName"
                name="fullName"
                type="text"
                value={createFormik.values.fullName}
                onChange={createFormik.handleChange}
                onBlur={createFormik.handleBlur}
                invalid={!!(createFormik.touched.fullName && createFormik.errors.fullName)}
                placeholder="Enter full name"
              />
              {createFormik.touched.fullName && createFormik.errors.fullName && (
                <FormFeedback>{createFormik.errors.fullName}</FormFeedback>
              )}
            </div>

            <div className="mb-3">
              <Label for="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={createFormik.values.email}
                onChange={createFormik.handleChange}
                onBlur={createFormik.handleBlur}
                invalid={!!(createFormik.touched.email && createFormik.errors.email)}
                placeholder="Enter email"
              />
              {createFormik.touched.email && createFormik.errors.email && (
                <FormFeedback>{createFormik.errors.email}</FormFeedback>
              )}
            </div>

            <div className="mb-3">
              <Label for="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={createFormik.values.password}
                onChange={createFormik.handleChange}
                onBlur={createFormik.handleBlur}
                invalid={!!(createFormik.touched.password && createFormik.errors.password)}
                placeholder="Enter password"
              />
              {createFormik.touched.password && createFormik.errors.password && (
                <FormFeedback>{createFormik.errors.password}</FormFeedback>
              )}
            </div>

            <div className="mb-3">
              <Label for="phoneNumber">Phone Number</Label>
              <PhoneInput
                country="pk"
                enableSearch
                containerClass="w-100"
                inputProps={{ name: "phoneNumber", id: "phoneNumber" }}
                inputClass={
                  createFormik.touched.phoneNumber && createFormik.errors.phoneNumber
                    ? "is-invalid"
                    : ""
                }
                value={createFormik.values.phoneNumber}
                onBlur={createFormik.handleBlur}
                onChange={(value) => createFormik.setFieldValue("phoneNumber", value)}
              />
              {createFormik.touched.phoneNumber && createFormik.errors.phoneNumber && (
                <div className="invalid-feedback d-block">{createFormik.errors.phoneNumber}</div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => {
                setShowCreateModal(false);
                createFormik.resetForm();
              }}
              disabled={createSubAdminMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={createSubAdminMutation.isPending || !createFormik.isValid}
            >
              {createSubAdminMutation.isPending ? "Creating..." : "Create"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Update Sub Admin Modal */}
      <Modal
        isOpen={showUpdateModal}
        toggle={() => {
          setShowUpdateModal(false);
          setSelectedSubAdmin(null);
          updateFormik.resetForm();
        }}
      >
        <ModalHeader
          toggle={() => {
            setShowUpdateModal(false);
            setSelectedSubAdmin(null);
            updateFormik.resetForm();
          }}
        >
          Update Sub Admin
        </ModalHeader>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            updateFormik.handleSubmit();
          }}
        >
          <ModalBody>
            <div className="mb-3">
              <Label for="update-fullName">Full Name</Label>
              <Input
                id="update-fullName"
                name="fullName"
                type="text"
                value={updateFormik.values.fullName}
                onChange={updateFormik.handleChange}
                onBlur={updateFormik.handleBlur}
                invalid={!!(updateFormik.touched.fullName && updateFormik.errors.fullName)}
                placeholder="Enter full name"
              />
              {updateFormik.touched.fullName && updateFormik.errors.fullName && (
                <FormFeedback>{updateFormik.errors.fullName}</FormFeedback>
              )}
            </div>

            <div className="mb-3">
              <Label for="update-email">Email</Label>
              <Input
                id="update-email"
                name="email"
                type="email"
                value={updateFormik.values.email}
                onChange={updateFormik.handleChange}
                onBlur={updateFormik.handleBlur}
                invalid={!!(updateFormik.touched.email && updateFormik.errors.email)}
                placeholder="Enter email"
              />
              {updateFormik.touched.email && updateFormik.errors.email && (
                <FormFeedback>{updateFormik.errors.email}</FormFeedback>
              )}
            </div>

            <div className="mb-3">
              <Label for="update-phoneNumber">Phone Number</Label>
              <PhoneInput
                country="pk"
                enableSearch
                containerClass="w-100"
                inputProps={{ name: "phoneNumber", id: "update-phoneNumber" }}
                inputClass={
                  updateFormik.touched.phoneNumber && updateFormik.errors.phoneNumber
                    ? "is-invalid"
                    : ""
                }
                value={updateFormik.values.phoneNumber}
                onBlur={updateFormik.handleBlur}
                onChange={(value) => updateFormik.setFieldValue("phoneNumber", value)}
              />
              {updateFormik.touched.phoneNumber && updateFormik.errors.phoneNumber && (
                <div className="invalid-feedback d-block">{updateFormik.errors.phoneNumber}</div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => {
                setShowUpdateModal(false);
                setSelectedSubAdmin(null);
                updateFormik.resetForm();
              }}
              disabled={updateSubAdminMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={updateSubAdminMutation.isPending || !updateFormik.isValid}
            >
              {updateSubAdminMutation.isPending ? "Updating..." : "Update"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default SubAdminList;
